// @flow
import * as React from 'react';
import RoleOperationEnum, {
    type RoleOperationEnumType
} from 'common/enums/RoleOperationEnum';
import usePersonnel from 'common/hooks/usePersonnel';
import { usePlatformSettings } from 'common/modules/platform-settings';
import ClientConfigService from 'common/services/ClientConfigService';

import { type Product } from './utils';

// Roles to access the settings
const ProductsRoleMap: {
    [k: Product]: RoleOperationEnumType
} = {
    platform: RoleOperationEnum.UPDATE_PLATFORM_SETTINGS,
    trackIQ: RoleOperationEnum.UPDATE_TRACK_IQ_SETTINGS,
    dispatchIQ: RoleOperationEnum.UPDATE_DISPATCHER_SETTINGS,
    orderIQ: RoleOperationEnum.UPDATE_ORDER_IQ_SETTINGS,
    orchestration: RoleOperationEnum.UPDATE_PARCEL_ORCHESTRATION_SETTINGS
};

// Client level product settings
const visibleSectionsMap = {
    trackIQ: 'showLiveViewSection',
    dispatchIQ: 'showBatchSection',
    orderIQ: 'showOrderManagementSection',
    orchestration: 'showOrchestrationSection'
};

type CanShow = (product: Product) => boolean;

const useCanShowProductSetting = (): CanShow => {
    const { sectionVisibilitySettings } = usePlatformSettings();
    const personnel = usePersonnel();

    // Checks permission role is present + RBAC is enabled
    const hasPermission = React.useCallback(
        (product: Product) => {
            const isRbacEnabled = ClientConfigService.isRbacEnabled();
            const hasUpdatePermission = personnel.hasPermission(
                ProductsRoleMap[product]
            );

            return isRbacEnabled && hasUpdatePermission;
        },
        [personnel]
    );

    const canShow = React.useCallback(
        (product: Product) => {
            const hasRoleAccess =
                hasPermission(product) || personnel.isGlobalPersonnel();

            // Platform doesn't have client settings
            if (product === 'platform') {
                return hasRoleAccess;
            }

            const sectionKey = visibleSectionsMap[product];
            // Check whether client settings has this product enabled
            const isProductEnabled = sectionVisibilitySettings[sectionKey];

            // Client settings + (role permission | MARA personnel)
            return isProductEnabled && hasRoleAccess;
        },
        [hasPermission, personnel, sectionVisibilitySettings]
    );

    return canShow;
};

export default useCanShowProductSetting;
